import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useVendorRole } from '../hooks/useVendorRole';
import { notificationStore } from '../services/store';

export default function Layout({ children }: { children: React.ReactNode }) {
  const { account, logout } = useAuth();
  const { role, scopedCategory, canManageContracts, canManageBilling, setRoleOverride } = useVendorRole();
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    setUnread(notificationStore.unreadCount());
    const interval = setInterval(() => setUnread(notificationStore.unreadCount()), 30_000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  const NAV = [
    { path: '/',              label: 'Vendors',       icon: '🏢', show: true },
    { path: '/dashboard',     label: 'Dashboard',     icon: '📊', show: true },
    { path: '/contracts',     label: 'Contracts',     icon: '📄', show: canManageContracts || role === 'DeptManager' },
    { path: '/billing',       label: 'Billing',       icon: '💰', show: canManageBilling },
    { path: '/reports',       label: 'Reports',       icon: '📈', show: true },
    { path: '/notifications', label: 'Notifications', icon: '🔔', show: true, badge: unread },
  ].filter(n => n.show);

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans">
      {/* Sidebar */}
      <aside className="w-[220px] shrink-0 flex flex-col bg-[#1a5c2a] text-white">
        {/* Brand */}
        <div className="px-4 pt-5 pb-3.5 border-b border-white/10">
          <div className="text-xs font-bold tracking-widest opacity-70 uppercase">Christmas Lumber</div>
          <div className="text-base font-bold mt-0.5">Vendor Manager</div>
          {scopedCategory && (
            <div className="text-[10px] opacity-60 mt-0.5">{scopedCategory} dept.</div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-3">
          {NAV.map(item => {
            const active = item.path === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={[
                  'flex items-center gap-2.5 px-3 py-2.5 rounded-lg mb-0.5',
                  'text-sm text-white no-underline transition-colors',
                  active ? 'bg-white/15 font-semibold' : 'font-normal hover:bg-white/10',
                ].join(' ')}
              >
                <span>{item.icon}</span>
                <span className="flex-1">{item.label}</span>
                {(item.badge ?? 0) > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center shrink-0">
                    {(item.badge ?? 0) > 9 ? '9+' : item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Role switcher (dev/demo — Phase 1 only) */}
        <div className="px-3 py-2 border-t border-white/10">
          <div className="text-[10px] font-semibold uppercase tracking-widest opacity-50 mb-1">Role (dev override)</div>
          <select
            value={localStorage.getItem('vm_role_override') ?? 'VendorManager'}
            onChange={e => setRoleOverride(e.target.value)}
            className="w-full bg-white/10 text-white text-xs rounded px-2 py-1 border border-white/20 outline-none cursor-pointer"
          >
            <option value="VendorManager">Vendor Manager</option>
            <option value="DeptManager:Accounts Payable">DM — Accounts Payable</option>
            <option value="DeptManager:Tech/IT">DM — Tech/IT</option>
            <option value="DeptManager:Operations">DM — Operations</option>
            <option value="DeptManager:Truss">DM — Truss</option>
            <option value="Viewer">Viewer</option>
          </select>
        </div>

        {/* User / sign-out */}
        <div className="px-4 py-3 border-t border-white/10 text-xs">
          <div className="opacity-80 mb-0.5 truncate">{account?.name}</div>
          <div className="opacity-55 mb-2 text-[11px] truncate">{account?.username}</div>
          <button
            onClick={logout}
            className="w-full bg-white/10 hover:bg-white/20 text-white border-0 py-1.5 px-3 rounded-md cursor-pointer text-xs transition-colors"
          >
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">{children}</main>
    </div>
  );
}
